import moment from 'moment'
import type { Moment } from 'moment'
import { PlaceModel } from '@/app/models/placeModel'
import { CountryModel } from '@/app/models/timesheet/countryModel'

// Kod 255 „reszta świata” - generator delegacji go pomija (jak WPF), więc wykres też.
const REST_OF_THE_WORLD = 'WLD'

const FAROE_ISLANDS_NATION_CODE = 20
const FRANCE_NATION_CODE = 17

const DAY_MS = 24 * 60 * 60 * 1000

// WPF SortedListGetEventsBetweenCovering: places overlapping the range, earliest first.
const placesCovering = (
  places: ReadonlyArray<PlaceModel>,
  from: number,
  to: number,
): Array<PlaceModel> =>
  places
    .map((place) => {
      const start = moment(place.startTime).valueOf()
      const end = place.endTime ? moment(place.endTime).valueOf() : start
      return { place, start, end }
    })
    .filter(({ start, end }) => end > from && start < to)
    .sort((a, b) => a.start - b.start)
    .map(({ place }) => place)

// WPF DriverPlaceSelector.GetCountryByNationCode
const countryOfPlace = (
  place: PlaceModel | undefined,
  countries: ReadonlyArray<CountryModel>,
): CountryModel | null => {
  if (!place) return null
  const nationCode =
    place.nationCode === FAROE_ISLANDS_NATION_CODE
      ? FRANCE_NATION_CODE
      : place.nationCode
  return countries.find((c) => c.nationId === nationCode) ?? null
}

// WPF DriverPlaceSelector.GetFirstCountryAfterTime: first place within 24 h after the time.
export const firstCountryAfter = (
  places: ReadonlyArray<PlaceModel> | undefined,
  time: Moment,
  countries: ReadonlyArray<CountryModel> | undefined,
): CountryModel | null => {
  if (!places?.length || !countries?.length) return null
  const from = time.valueOf()
  return countryOfPlace(
    placesCovering(places, from, from + DAY_MS)[0],
    countries,
  )
}

// WPF DriverPlaceSelector.GetFirstCountryBeforeTime: last place within 24 h before the time.
export const lastCountryBefore = (
  places: ReadonlyArray<PlaceModel> | undefined,
  time: Moment,
  countries: ReadonlyArray<CountryModel> | undefined,
): CountryModel | null => {
  if (!places?.length || !countries?.length) return null
  const to = time.valueOf()
  const covering = placesCovering(places, to - DAY_MS, to)
  return countryOfPlace(covering[covering.length - 1], countries)
}

// Web addition (#3159): without a preceding country change WPF falls back to Poland.
// Places without a known country (code 255) must not hide an earlier one.
export const driverCountryAt = (
  places: ReadonlyArray<PlaceModel> | undefined,
  time: Moment,
  countries: ReadonlyArray<CountryModel> | undefined,
): CountryModel | null => {
  if (!places?.length || !countries?.length) return null
  const known = places.filter((place) => countryOfPlace(place, countries))
  return (
    lastCountryBefore(known, time, countries) ??
    firstCountryAfter(known, time, countries)
  )
}

// Wpisy kierowcy, granice z GNSS i pozycje z tachografu zlane w jedną oś czasu:
// flaga tylko przy zmianie kraju, a wygrywa najwcześniejszy wpis niezależnie od źródła.
// Pierwszy wpis od visibleFrom zawsze dostaje flagę, żeby widoczne okno pokazywało bieżący kraj.
export const toCountryChangePlaces = (
  places: ReadonlyArray<PlaceModel> | undefined,
  visibleFrom?: Moment | null,
): Array<PlaceModel> => {
  if (!places?.length) return []
  const visibleFromTime = visibleFrom ? visibleFrom.valueOf() : null
  const sorted = places
    .map((place) => ({ place, time: moment(place.startTime).valueOf() }))
    .sort((a, b) => a.time - b.time)
  const result: Array<PlaceModel> = []
  let currentNation: string | null = null
  let seeded = visibleFromTime == null
  for (const { place, time } of sorted) {
    if (!seeded && time >= visibleFromTime) {
      seeded = true
      currentNation = null
    }
    const nation = place.nation?.toUpperCase()
    if (!nation || nation === REST_OF_THE_WORLD || nation === currentNation)
      continue
    currentNation = nation
    result.push(place)
  }
  return result
}
